/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import type { TextStyle } from 'react-native';
import {
  getColor,
  getFontFamily,
  getFontWeight,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import type { Font, Colour } from '@airasia-phoenix/react-native/utils/utils';

// The app ships only two DM Sans files — Regular (400) and Medium (500) — and
// getFontFamily maps any weight >= 500 to the DMSans-Medium family. Phoenix
// heading tokens carry weight 600, which browsers synthesize into a faux bold
// on top of the Medium file, rendering heavier than the Figma. Clamp the CSS
// weight to the loaded file's real weight on web; native ignores the synthetic
// weight and already renders the Medium glyphs.
const resolveFontWeight = (font: Font): TextStyle['fontWeight'] =>
  Platform.OS === 'web'
    ? getFontFamily(font) === 'DMSans-Medium'
      ? '500'
      : '400'
    : getFontWeight(font.weight);

/**
 * getTextStyle with the web font weight clamped to the loaded font file (see
 * resolveFontWeight). Use for headings that otherwise render faux-bold on web.
 */
export const getPlatformTextStyle = (font: Font, colour: Colour) => ({
  ...(getTextStyle(font, colour) as TextStyle),
  fontWeight: resolveFontWeight(font),
});

/**
 * Text style for unistyles stylesheets that switches typescale per breakpoint:
 * `mobile` applies below the `md` breakpoint (the same 768px cutoff as
 * useDeviceInfo().isMobileWidth) and `desktop` from `md` up, so mobile web and
 * the native app share the mobile typography. Only usable inside
 * StyleSheet.create — plain style arrays cannot resolve the breakpoint records.
 */
export const responsiveTextStyle = (
  mobile: Font,
  desktop: Font,
  colour: Colour,
) => ({
  color: getColor(colour),
  fontFamily: { xs: getFontFamily(mobile), md: getFontFamily(desktop) },
  fontSize: { xs: mobile.size, md: desktop.size },
  fontWeight: {
    xs: resolveFontWeight(mobile),
    md: resolveFontWeight(desktop),
  },
  lineHeight: { xs: mobile.line_height, md: desktop.line_height },
});
